import { useState, useCallback, useMemo } from "react";

export interface ColumnDefinition {
  id: string;
  labelKey: string;
  accessor: string;
  sortable: boolean;
  align?: "left" | "right" | "center";
  defaultVisible?: boolean;
}

export interface SortConfig {
  column: string;
  direction: "asc" | "desc";
}

interface ColumnState {
  visibleColumns: string[];
  columnOrder: string[];
  sortConfig: SortConfig | null;
}

const DEFAULT_COLUMNS: ColumnDefinition[] = [
  { id: "name", labelKey: "salesReport.columns.dealName", accessor: "name", sortable: true, align: "left", defaultVisible: true },
  { id: "contact_name", labelKey: "salesReport.columns.client", accessor: "contact_name", sortable: true, align: "left", defaultVisible: true },
  { id: "total_amount", labelKey: "salesReport.columns.totalValue", accessor: "total_amount", sortable: true, align: "right", defaultVisible: true },
  { id: "closed_date", labelKey: "salesReport.columns.closedDate", accessor: "closed_date", sortable: true, align: "left", defaultVisible: true },
  { id: "deal_status", labelKey: "salesReport.columns.dealStatus", accessor: "deal_status", sortable: true, align: "center", defaultVisible: true },
  { id: "payment_status", labelKey: "salesReport.columns.paymentStatus", accessor: "payment_status", sortable: true, align: "center", defaultVisible: true },
  { id: "contract_status", labelKey: "salesReport.columns.contractStatus", accessor: "contract_status", sortable: true, align: "center", defaultVisible: true },
  { id: "card_status", labelKey: "salesReport.columns.cardStatus", accessor: "card_status", sortable: true, align: "center", defaultVisible: false },
  { id: "auto_charge_status", labelKey: "salesReport.columns.autoChargeStatus", accessor: "auto_charge_status", sortable: true, align: "center", defaultVisible: true },
  { id: "sales_consultant_name", labelKey: "salesReport.columns.seller", accessor: "sales_consultant_name", sortable: true, align: "left", defaultVisible: true },
  { id: "sdr_name", labelKey: "salesReport.columns.sdr", accessor: "sdr_name", sortable: true, align: "left", defaultVisible: true },
  { id: "deals_source", labelKey: "salesReport.columns.source", accessor: "deals_source", sortable: true, align: "left", defaultVisible: false },
  { id: "deals_channel", labelKey: "salesReport.columns.channel", accessor: "deals_channel", sortable: true, align: "left", defaultVisible: false },
  { id: "actions", labelKey: "salesReport.columns.actions", accessor: "id", sortable: false, align: "center", defaultVisible: true },
];

const STORAGE_KEY = "salesReportColumns";

const getDefaultState = (): ColumnState => ({
  visibleColumns: DEFAULT_COLUMNS.filter(c => c.defaultVisible).map(c => c.id),
  columnOrder: DEFAULT_COLUMNS.map(c => c.id),
  sortConfig: null,
});

const loadFromStorage = (): ColumnState => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch {
    // Ignore parsing errors
  }
  return getDefaultState();
};

const saveToStorage = (state: ColumnState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
};

export function useSalesReportColumns() {
  const [state, setState] = useState<ColumnState>(loadFromStorage);

  const updateState = useCallback((newState: Partial<ColumnState>) => {
    setState(prev => {
      const updated = { ...prev, ...newState };
      saveToStorage(updated);
      return updated;
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId: string) => {
    setState(prev => {
      const isVisible = prev.visibleColumns.includes(columnId);
      const visibleColumns = isVisible
        ? prev.visibleColumns.filter(id => id !== columnId)
        : [...prev.visibleColumns, columnId];
      
      const updated = { ...prev, visibleColumns };
      saveToStorage(updated);
      return updated;
    });
  }, []);

  const reorderColumns = useCallback((newOrder: string[]) => {
    updateState({ columnOrder: newOrder });
  }, [updateState]);

  const toggleSort = useCallback((columnId: string) => {
    setState(prev => {
      let sortConfig: SortConfig | null;
      
      if (prev.sortConfig?.column !== columnId) {
        sortConfig = { column: columnId, direction: "asc" };
      } else if (prev.sortConfig.direction === "asc") {
        sortConfig = { column: columnId, direction: "desc" };
      } else {
        sortConfig = null;
      }
      
      const updated = { ...prev, sortConfig };
      saveToStorage(updated);
      return updated;
    });
  }, []);

  const resetToDefault = useCallback(() => {
    const defaultState = getDefaultState();
    setState(defaultState);
    saveToStorage(defaultState);
  }, []);

  const orderedVisibleColumns = useMemo(() => {
    return state.columnOrder
      .filter(id => state.visibleColumns.includes(id))
      .map(id => DEFAULT_COLUMNS.find(col => col.id === id)!)
      .filter(Boolean);
  }, [state.columnOrder, state.visibleColumns]);

  const allColumns = useMemo(() => {
    return state.columnOrder
      .map(id => DEFAULT_COLUMNS.find(col => col.id === id)!)
      .filter(Boolean);
  }, [state.columnOrder]);

  return {
    columns: DEFAULT_COLUMNS,
    visibleColumns: state.visibleColumns,
    columnOrder: state.columnOrder,
    sortConfig: state.sortConfig,
    orderedVisibleColumns,
    allColumns,
    toggleColumnVisibility,
    reorderColumns,
    toggleSort,
    resetToDefault,
  };
}
